// src/api/features/users/mutations.ts
import {
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createUser,
  updateUser,
  deleteUser,
  grantUserModules,
  revokeUserModules,
} from "./api";

import type {
  CreateUserBody,
  UpdateUserBody,
  PaginatedUsers,
  UserResult,
} from "./types";
import type { Module } from "@/api/features/modules/types";

import api from "@/api/client";
import { PATHS } from "@/api/constants";
import { UsersKeys, userModulesKey } from "./queries";

/* =====================================
 * Local helper types (for mutation context)
 * ===================================== */

type UsersListSnapshot = {
  key: readonly unknown[];
  data: PaginatedUsers | undefined;
};

type UpdateUserContext = {
  prevDetail?: UserResult;
  prevLists?: UsersListSnapshot[];
};

type DeleteUserContext = {
  prevLists?: UsersListSnapshot[];
};

/* =====================================
 * User CRUD mutations
 * ===================================== */

/** 🔹 Create user */
export function useCreateUserMutation() {
  const qc = useQueryClient();

  return useMutation<UserResult, unknown, CreateUserBody>({
    mutationFn: (payload: CreateUserBody) => createUser(payload),
    onSuccess: (created) => {
      qc.setQueryData<UserResult>(UsersKeys.detail(created.id), created);
      qc.invalidateQueries({ queryKey: UsersKeys.all });
    },
  });
}

/** 🔹 Update user */
export function useUpdateUserMutation() {
  const qc = useQueryClient();

  return useMutation<
    UserResult,
    unknown,
    { id: number | string; data: UpdateUserBody },
    UpdateUserContext
  >({
    mutationFn: ({ id, data }) => updateUser(id, data),

    async onMutate({ id, data }) {
      // Cancel relevant queries for this user + all user lists
      await Promise.all([
        qc.cancelQueries({ queryKey: UsersKeys.detail(id) }),
        qc.cancelQueries({ queryKey: UsersKeys.all }),
      ]);

      const prevDetail = qc.getQueryData<UserResult>(UsersKeys.detail(id));
      const prevLists: UsersListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: UsersKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedUsers | undefined,
        }));

      // Optimistic detail patch
      if (prevDetail) {
        qc.setQueryData<UserResult>(UsersKeys.detail(id), {
          ...prevDetail,
          ...data,
        });
      }

      // Optimistic list patch
      for (const snap of prevLists) {
        const list = snap.data;
        if (!list?.items) continue;

        const patched = list.items.map((u) =>
          u.id === Number(id) ? ({ ...u, ...data } as UserResult) : u
        );

        qc.setQueryData<PaginatedUsers>(snap.key, {
          ...list,
          items: patched,
        });
      }

      return { prevDetail, prevLists };
    },

    onError(_err, _vars, ctx) {
      if (!ctx) return;

      if (ctx.prevDetail) {
        qc.setQueryData<UserResult>(
          UsersKeys.detail(ctx.prevDetail.id),
          ctx.prevDetail
        );
      }

      for (const snap of ctx.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSuccess(updated, { id }) {
      qc.setQueryData<UserResult>(UsersKeys.detail(id), updated);
    },

    onSettled(_res, _err, { id }) {
      qc.invalidateQueries({ queryKey: UsersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: UsersKeys.all });
    },
  });
}


/** 🔹 Delete user */
export function useDeleteUserMutation() {
  const qc = useQueryClient();

  return useMutation<{ message: string }, unknown, number | string, DeleteUserContext>({
    mutationFn: (id: number | string) => deleteUser(id),

    async onMutate(id) {
      await qc.cancelQueries({ queryKey: UsersKeys.all });

      const numericId = Number(id);
      const prevLists: UsersListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: UsersKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedUsers | undefined,
        }));

      for (const snap of prevLists) {
        const data = snap.data;
        if (!data?.items) continue;

        qc.setQueryData<PaginatedUsers>(snap.key, {
          ...data,
          items: data.items.filter((u) => u.id !== numericId),
          total: Math.max(0, (data.total ?? 1) - 1),
        });
      }

      qc.removeQueries({ queryKey: UsersKeys.detail(id) });

      return { prevLists };
    },

    onError(_err, _id, ctx) {
      for (const snap of ctx?.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSettled() {
      qc.invalidateQueries({ queryKey: UsersKeys.all });
    },
  });
}

/* =====================================
 * User ↔ Module access mutations
 * ===================================== */

/** 🔹 Grant module access to a user */
export function useGrantUserModulesMutation() {
  const qc = useQueryClient();

  return useMutation<Module[], unknown, { userId: number | string; moduleIds: number[] }>({
    mutationFn: (payload) =>
      grantUserModules(payload.userId, payload.moduleIds),

    onSuccess(modules, payload) {
      qc.setQueryData<Module[]>(userModulesKey(payload.userId), modules);
      // Optionally: qc.invalidateQueries({ queryKey: UsersKeys.detail(payload.userId) });
    },
  });
}

/** 🔹 Revoke module access from a user */
export function useRevokeUserModulesMutation() {
  const qc = useQueryClient();

  return useMutation<Module[], unknown, { userId: number | string; moduleIds: number[] }>({
    mutationFn: (payload) =>
      revokeUserModules(payload.userId, payload.moduleIds),

    onSuccess(modules, payload) {
      qc.setQueryData<Module[]>(userModulesKey(payload.userId), modules);
    },
  });
}

/* =====================================
 * Reset password mutation
 * ===================================== */

/**
 * 🔹 Send password reset link email for a user.
 *
 * Call this with the user's email:
 *   const resetMut = useResetUserPasswordMutation();
 *   resetMut.mutate(user.email);
 */
export function useResetUserPasswordMutation() {
  return useMutation({
    mutationFn: async (email: string) => {
      const res = await api.post(PATHS.auth.requestPasswordReset, { email });
      return res.data;
    },
  });
}
